import React, { useState } from 'react';

import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useMutationDeleteTemplate } from 'api/mutations/deleteTemplate.mutation';

import Icon from 'components/common/Icons/base/Icon/Icon';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import { portfolioConfig } from 'components/Dashboards/Portfolio/portfolioConfig';
import { CustomizedConfirmDeleteModal, CustomizedRoundIcon, CustomizedTooltip } from 'components/common';
import { ITemplate } from 'components/pages/PhasePerformanceManagePage/components/Manage/Manage.types';
import classes from 'components/pages/PhasePerformanceManagePage/components/Manage/components/SingleTemplateComponent/SingleTemplateComponent.module.scss';
import { TemplateProjectsComponent } from 'components/pages/PhasePerformanceManagePage/components/Manage/components/SingleTemplateComponent/components/TemplateProjectsComponent';

export const SingleTemplateComponent = ({
    template,
    isOpenInitial = false,
}: {
    template: ITemplate;
    isOpenInitial?: boolean;
}) => {
    const [isOpen, setIsOpen] = useState<boolean>(isOpenInitial);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();

    const mutation = useMutationDeleteTemplate();

    const handleDeleteTemplate = () => {
        mutation.mutate(template.id, {
            onSuccess: () => {
                handleSnackBar('Successfully deleted!', 'success');
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
            onSettled: () => {
                Promise.all([
                    queryClient.invalidateQueries('phasePerformanceTemplates'),
                    queryClient.invalidateQueries('getTemplateNames'),
                ]).then(() => {
                    setShowDeleteConfirm(false);
                });
            },
        });
    };

    const handleToggleProjects = () => {
        setIsOpen(!isOpen);
    };

    return (
        <div data-testid={'single-template-container'} className={classes.singleTemplateContainer}>
            <div data-testid={'template-container'} className={classes.templateContent}>
                <div data-testid={'template-content'} className={classes.content}>
                    <div
                        data-testid={'template-title'}
                        className={classes.title}
                        id={`manage_phase_template_name_${template.name}`}
                    >
                        Template: {template.name}
                    </div>
                    <div className={classes.projectSCount}> {template?.projects?.length} projects </div>
                </div>
                <div className={classes.actionIcon}>
                    <Link
                        className={classes.linkStyle}
                        to={`${portfolioConfig.phasePerformanceManage.link}/${portfolioConfig.phasePerformanceManageAddTemplate.link}?templateId=${template.id}`}
                        id={`manage_phase_template_${template.name}_edit`}
                    >
                        <CustomizedTooltip
                            tooltipContent={'Edit Template'}
                            triggerElement={
                                <CustomizedRoundIcon>
                                    <Icon name={'edit-user-profile'} />
                                </CustomizedRoundIcon>
                            }
                        />
                    </Link>

                    <CustomizedTooltip
                        tooltipContent={'Delete Template'}
                        triggerElement={
                            <CustomizedRoundIcon
                                onClick={() => setShowDeleteConfirm(true)}
                                id={`manage_phase_template_${template.name}_delete`}
                            >
                                <MuiIcon icon={'delete'} type={'outlined'} />
                            </CustomizedRoundIcon>
                        }
                    />

                    <CustomizedTooltip
                        tooltipContent={isOpen ? 'Close Projects' : 'Open Projects'}
                        triggerElement={
                            <CustomizedRoundIcon
                                onClick={handleToggleProjects}
                                id={`manage_phase_template_${template.name}_toggle`}
                            >
                                <Icon name={isOpen ? 'header-arrow-top' : 'header-arrow-down'} />
                            </CustomizedRoundIcon>
                        }
                    />
                </div>
            </div>

            <CustomizedConfirmDeleteModal
                isShowModal={showDeleteConfirm}
                hideModal={() => setShowDeleteConfirm(false)}
                text={`Are you sure you want to delete this template?`}
                onConfirm={() => {
                    handleDeleteTemplate();
                }}
                isLoading={mutation?.isLoading}
            />

            {isOpen && <TemplateProjectsComponent projects={template.projects || []} templateId={template.id} />}
        </div>
    );
};
